<template>
  <div class="border rounded">
    <div
      class="mb-2 border-bottom d-flex align-items-center justify-content-center"
      :class="headerClass"
    >
      <svg-icon v-if="icon" :icon="icon" class="icon-lg mr-1" />
      <span v-if="title.length" class="d-inline-block reward-title">{{
        title
      }}</span>
      <div
        v-else
        :class="{
          'form-name-error': errors.name
        }"
      >
        <input
          type="text"
          data-cy="reward-name"
          :placeholder="getPlaceholderText(errors.name)"
          v-model="edit.name"
          :class="headerClass"
          :disabled="!isEdit"
          class="border-0 reward-title w-100"
        />
      </div>
    </div>
    <div class="reward-body txt-6">
      <vue-editor
        v-if="isEdit && !isPlainDesc"
        :class="{
          'ct-editor ct-editor-sm ct-editor-simple mb-3': true,
          'form-item-error': errors.description
        }"
        :editor-toolbar="rewardEditor.customToolbar"
        v-model="edit.description"
        v-b-tooltip.hover
        :title="errors.description"
      />
      <div v-if="!isEdit" v-html="edit.description"></div>
      <textarea
        class="mb-4 txt-5-alt"
        :class="{ 'form-item-error': errors.description }"
        data-cy="reward-description"
        :placeholder="$t('description')"
        v-model="edit.description"
        :disabled="!isEdit || disabled"
        v-if="
          (value.description !== '' || value.description !== null) &&
            isEdit &&
            isPlainDesc
        "
        @input="autosize($event)"
        @focus="autosize($event)"
        v-b-tooltip.hover
        :title="errors.description && errors.description"
      ></textarea>
      <b-form-row v-if="isEdit" class="mb-2">
        <b-col v-for="(limit, i) in edit.limits" :key="i">
          <b-form-group class="mb-2">
            <legend class="col-form-label pt-0 pb-0 text-uppercase">
              {{ $t(limitTypes[`${limit.type}`]) }} {{ $t('limit') }}
            </legend>
            <div class="d-flex">
              <b-input-number
                size="sm"
                :placeholder="$t('min').toUpperCase()"
                type="text"
                :disabled="disabled"
                v-model.number="edit.limits[i].min"
                class="p-0 text-5-alt border-top-0 border-left-0 border-right-0 rounded-0 shadow-none"
              />
              <div class="ml-1 mr-1 text-lighten">-</div>
              <b-input-number
                size="sm"
                :placeholder="$t('max').toUpperCase()"
                :disabled="disabled"
                v-model.number="edit.limits[i].max"
                type="text"
                class="p-0 text-5-alt border-top-0 border-left-0 border-right-0 rounded-0 shadow-none"
              />
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row v-if="!isEdit && !noLimits" class="mb-2">
        <b-col v-for="(limit, i) in value.limits" :key="i">
          <b-form-group :class="'mb-0'">
            <legend class="col-form-label pt-0 pb-0 text-uppercase">
              {{ $t(`${limitTypes[limit.type]}Limit`) }}
            </legend>
            <div class="d-flex">
              {{ value.limits[i].min }}
              <div class="ml-1 mr-1 text-lighten">-</div>
              {{ value.limits[i].max }}
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>
      <legend class="col-form-label pt-0 pb-0 text-uppercase">
        {{ $tc('reward', hasSlots ? 2 : 1) }}
      </legend>
      <template v-if="isEdit">
        <b-form-row
          v-for="(slot, i) in edit.slots"
          :key="i"
          class="reward-pricing mb-2"
          data-cy="subtask-slot"
        >
          <b-col cols="9">
            <input
              v-if="hasSlots"
              :data-cy="`slot-${i}-name`"
              type="text"
              v-model="slot.description"
              :disabled="!isEdit"
              :class="{
                'slot-name': true,
                'border-0': !isEdit,
                'form-item-error':
                  errors[`slots[${i}]`] && errors[`slots[${i}]`].description
              }"
              :placeholder="
                errors[`slots[${i}]`] && errors[`slots[${i}]`].description
                  ? errors[`slots[${i}]`].description
                  : $t('nameOfReward')
              "
            />
          </b-col>
          <b-col :cols="hasSlots ? 3 : 12" class="d-flex">
            <b-form-group
              :data-cy="`slot-${i}-amount-group`"
              class="small flex-grow-1 mb-0"
              v-b-tooltip.hover
              :title="
                errors[`slots[${i}]`] &&
                  errors[`slots[${i}]`].reward &&
                  errors[`slots[${i}]`].reward.amount
              "
            >
              <b-input-number
                :data-cy="`slot-${i}-amount`"
                size="sm"
                v-model.number="slot.reward.amount"
                :disabled="disabled"
                @input="handleEmptyString($event, i)"
                class="p-0 text-5-alt border-top-0 border-left-0 border-right-0 rounded-0 shadow-none"
                :class="{
                  'form-item-error':
                    errors[`slots[${i}]`] &&
                    errors[`slots[${i}]`].reward &&
                    errors[`slots[${i}]`].reward.amount
                }"
                style="height: 30px;"
                type="text"
              />
            </b-form-group>
            <button
              v-if="i > 0"
              @click="removeSlot(i)"
              class="border-0 btn-link-alt ml-2"
              v-b-tooltip.hover
              :title="$t('removeSlot')"
            >
              <svg-icon icon="cross" class="icon-xs" />
            </button>
          </b-col>
        </b-form-row>
      </template>
      <template v-else>
        <div
          v-for="(slot, i) in edit.slots"
          :key="i"
          class="reward-pricing mb-2"
        >
          <span v-if="hasSlots">{{ slot.description }}</span> -
          <strong v-if="slot.reward">{{ slot.reward.amount }}</strong>
          <strong v-else>{{ $t('notSet') }}</strong>
        </div>
      </template>
      <button
        v-if="isEdit && hasSlots"
        class="add-slot"
        @click="addSlot()"
        data-cy="add-slot"
      >
        <svg-icon icon="plus" class="icon-xs mr-1 align-middle" />
        <span class="align-middle small font-weight-bold">
          {{ $t('addSlot') | upperCase }}
        </span>
      </button>
      <slot name="bottom"></slot>
    </div>
    <div
      v-if="isRemovable"
      class="pb-4 pr-4 pl-4 d-flex justify-content-end"
      @click.prevent="$emit('remove-subtask', { index: index })"
    >
      <button class="btn btn-link-alt btn-sm">{{ $t('delete') }}</button>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'clone';
import isEqual from 'js-isequal';

export default {
  props: {
    value: {
      type: Object,
      required: true
    },
    noLimits: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    isRemovable: {
      type: Boolean,
      default: false
    },
    icon: {
      validator(value) {
        return (
          value === null || typeof value === 'string' || value instanceof String
        );
      },
      default: null
    },
    title: {
      type: String,
      default: ''
    },
    headerClass: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hasCustomTitle: {
      type: Boolean,
      default: false
    },
    isPlainDesc: {
      type: Boolean,
      default: false
    },
    hasSlots: {
      type: Boolean,
      default: true
    },
    errors: {
      type: Object,
      default() {
        return {};
      }
    },
    name: {
      type: String,
      default: ''
    },
    index: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      edit: cloneDeep(this.value),
      limitTypes: {
        '1': 'daily',
        '7': 'weekly'
      },
      rewardEditor: {
        customToolbar: [
          [
            'bold',
            'italic',
            'underline',
            'strike',
            'link',
            { list: 'ordered' },
            { list: 'bullet' }
          ]
        ]
      }
    };
  },

  methods: {
    autosize(e) {
      const el = e.target;
      el.style.cssText = 'height: auto; padding: 0;';
      el.style.cssText = 'height:' + el.scrollHeight + 'px';
    },

    addSlot() {
      this.edit.slots.push({ description: '', reward: { amount: null } });
    },

    removeSlot(index) {
      this.edit.slots.splice(index, 1);
    },

    handleEmptyString(val, i) {
      if (val === '') this.edit.slots[i].reward.amount = null;
    },

    getPlaceholderText(error) {
      return error ? this.$t('enterName') : this.$t('subtaskName');
    }
  },

  watch: {
    value: {
      handler() {
        this.edit = cloneDeep(this.value);
      }
    },
    edit: {
      deep: true,
      handler(val) {
        if (this.name === '') {
          throw new Error('you must pass "name" prop for editing reward');
        }
        if (!isEqual(val, this.value)) {
          const name = this.name === 'creative' ? this.index : this.name;
          this.$emit('input', { name, data: cloneDeep(val) });
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.reward-title {
  font-size: 18px;
  vertical-align: middle;
  text-align: center;
  line-height: 60px;

  &:disabled {
    background: none;
  }
}

.reward-body {
  padding: 1rem;
}

.reward-pricing {
  input {
    display: block;
    border: none;
    border-bottom: 1px solid $border-color;
    width: 100%;

    &:focus {
      border-color: $secondary;
    }

    &.form-item-error {
      border-color: $danger;
    }
  }
}

.btn-remove-reward {
  top: 0;
  right: 0;
  padding: 0;
  color: $primary;
}

.add-slot {
  color: $primary;
  border: none;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: $primary-hover;
  }
}

.slot-name.form-item-error::placeholder {
  color: $danger;
  font-size: 11px;
}

.form-name-error {
  input::placeholder {
    color: $danger;
  }
}

textarea {
  display: block;
  border: none;
  border-bottom: 1px solid $border-color;
  resize: none;
  width: 100%;
  color: $body-color;
  height: auto;
  overflow: hidden;
  padding: 0;
  box-sizing: padding-box;
  height: 54px;

  &:focus {
    border-color: $secondary;
  }

  &.form-item-error {
    border-color: $danger;
  }

  &:disabled {
    background-color: $disabled-color;
  }
}
</style>
